import React, { useEffect, useMemo, useState } from "react";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IDismantlingBay } from "../../models/DismantlingBay";
import { IPlannerVehicleRow } from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import Modal from "../../layouts/Modal";
import LoadingPulse from "../shared/LoadingPulse";
import { complexityLabel } from "../../utils/vehicleDisplay";

interface RebookModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vehicles: IPlannerVehicleRow[];
  isLoading: boolean;
  productionLineBays: IDismantlingBay[];
  onConfirm: (vehicleIds: string[], dismantlingBayId: string, plannedOn: string) => Promise<void>;
  isRebooking: boolean;
}

export default function RebookModal({
  open,
  setOpen,
  vehicles,
  isLoading,
  productionLineBays,
  onConfirm,
  isRebooking,
}: RebookModalProps) {
  const { t } = useTranslation("production");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bayId, setBayId] = useState<string>("");
  const [dateIso, setDateIso] = useState<string>(DateTime.local().toISODate() ?? "");

  // Reset selection and defaults each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(vehicles.map((v) => v.id)));
    setBayId((current) => current || productionLineBays[0]?.id || "");
    setDateIso(DateTime.local().toISODate() ?? "");
  }, [open, vehicles, productionLineBays]);

  const allSelected = vehicles.length > 0 && selectedIds.size === vehicles.length;

  const toggleVehicle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(vehicles.map((v) => v.id)));
  };

  const canSubmit = selectedIds.size > 0 && !!bayId && !!dateIso && !isRebooking;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onConfirm(Array.from(selectedIds), bayId, dateIso);
  };

  const sortedVehicles = useMemo(
    () =>
      [...vehicles].sort(
        (a, b) =>
          new Date(a.plannedDismantlingOn ?? 0).getTime() -
          new Date(b.plannedDismantlingOn ?? 0).getTime()
      ),
    [vehicles]
  );

  const selectClass =
    "text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";

  return (
    <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{t("Rebook vehicles")}</h1>
          <p className="text-sm text-gray-500">
            {t("Unfinished vehicles scheduled in the last 30 days")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {isLoading ? (
        <div className="mt-4">
          <LoadingPulse />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="mt-6 mb-2 text-center text-sm text-gray-500">
          {t("No unfinished vehicles from the last 30 days.")}
        </div>
      ) : (
        <>
          <div className="mt-4 border border-gray-200 rounded-md max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">{t("Vehicle")}</th>
                  <th className="px-3 py-2 text-left">{t("Scheduled")}</th>
                  <th className="px-3 py-2 text-left">{t("Progress")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedVehicles.map((v) => {
                  const scheduled = v.plannedDismantlingOn
                    ? DateTime.fromISO(v.plannedDismantlingOn, { zone: "utc" }).toFormat("ccc d LLL")
                    : "—";
                  return (
                    <tr
                      key={v.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleVehicle(v.id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={() => toggleVehicle(v.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-900">{v.vrm || t("No VRM")}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {[v.combinedMake, v.combinedModel].filter(Boolean).join(" ")}
                          <span className="ml-1">({complexityLabel(v.complexity)})</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{scheduled}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {t("{{removed}}/{{total}} parts", {
                          removed: v.partsRemoved,
                          total: v.partsTotal,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">
                {t("Line")}
              </label>
              <select
                className={selectClass}
                value={bayId}
                onChange={(e) => setBayId(e.target.value)}
              >
                {productionLineBays.length === 0 ? (
                  <option value="">{t("No production lines configured")}</option>
                ) : (
                  productionLineBays.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">
                {t("Date")}
              </label>
              <input
                type="date"
                value={dateIso}
                onChange={(e) => setDateIso(e.target.value)}
                className={selectClass}
              />
            </div>

            <div className="ml-auto text-sm text-gray-500 self-center">
              {t("{{count}} selected", { count: selectedIds.size })}
            </div>
          </div>
        </>
      )}

      <div className="flex items-center justify-end gap-x-2 pt-5">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canSubmit}
          className={classNames(
            "text-sm px-3 py-1.5 rounded-md bg-primary-600 text-white hover:bg-primary-700",
            !canSubmit ? "opacity-60 cursor-not-allowed" : ""
          )}
        >
          {isRebooking ? t("Rebooking...") : t("Rebook")}
        </button>
      </div>
    </Modal>
  );
}
